// The Finance list's row and vitals strings (mobile overhaul Phase C §8.1).
// Pure so the Node test suite can pin them. `invoiceDateMeta` is shared with
// the desktop render — both widths must read an invoice's due state the same
// way — while the mono vitals lines and the expense row parts exist only at
// ≤880px, where the four desktop vitals collapse to two cards plus one line.

import { formatDayMonth } from './calendar';
import { formatMoney, formatMoneyExact } from './moneyFormat';
import { INVOICE_KINDS, type InvoiceKind, type InvoiceStatus } from '../../shared/types';

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Whole days past due, floored at 1 — "due 3 Jul · 69d". */
export function daysOverdue(due: string | null, today: string): number | null {
  if (!due) return null;
  return Math.max(1, Math.round((Date.parse(today) - Date.parse(due)) / 86400000));
}

export interface InvoiceDateMeta {
  text: string;
  /** Only an overdue invoice is coloured; every other state reads as dim. */
  tone: 'danger' | 'dim';
}

/** The invoice row's date line, in the tone its status earns. `status` is the
    *display* status (a sent invoice past its due date reads as overdue). */
export function invoiceDateMeta(
  inv: { due_date: string | null; paid_date: string | null },
  status: InvoiceStatus,
  today: string,
): InvoiceDateMeta {
  switch (status) {
    case 'overdue':
      return {
        text: inv.due_date ? `due ${formatDayMonth(inv.due_date)} · ${daysOverdue(inv.due_date, today)}d` : 'overdue',
        tone: 'danger',
      };
    case 'sent':
      return { text: inv.due_date ? `due ${formatDayMonth(inv.due_date)}` : 'sent', tone: 'dim' };
    case 'paid':
      return { text: inv.paid_date ? `paid ${formatDayMonth(inv.paid_date)}` : 'paid', tone: 'dim' };
    case 'void':
      return { text: 'void', tone: 'dim' };
    default:
      return { text: 'not sent', tone: 'dim' };
  }
}

export interface VitalsLine {
  left: string;
  right: string;
}

/** "PAID FY27 Q1 · $4,000" / "INVOICED FY · $16,170 EX GST" — the two desktop
    vitals that don't survive as cards, carrying the same numbers. */
export function invoiceVitalsLine(v: {
  quarterKey: string;
  paidQuarterCents: number;
  invoicedFYCents: number;
  exGst: boolean;
}): VitalsLine {
  return {
    left: `PAID ${v.quarterKey} · ${formatMoney(v.paidQuarterCents)}`,
    right: `INVOICED FY · ${formatMoney(v.invoicedFYCents)} ${v.exGst ? 'EX GST' : 'INCL. GST'}`,
  };
}

/** "THIS FY · $2,184 · 212 ALL TIME" / "TOP · SOFTWARE". */
export function expenseVitalsLine(v: {
  fyCents: number;
  allTimeCount: number;
  topCategory: string | null;
}): VitalsLine {
  return {
    left: `THIS FY · ${formatMoney(v.fyCents)} · ${v.allTimeCount} ALL TIME`,
    right: `TOP · ${v.topCategory ? v.topCategory.toUpperCase() : '—'}`,
  };
}

/** The category with the most spend, or null when nothing is dated in range.
    Ties keep the first-seen category, matching the desktop vital. */
export function topExpenseCategory(rows: { category: string; amount: number }[]): string | null {
  const totals = new Map<string, number>();
  for (const row of rows) totals.set(row.category, (totals.get(row.category) ?? 0) + row.amount);
  let top: string | null = null;
  let best = -Infinity;
  for (const [category, total] of totals) {
    if (total > best) {
      top = category;
      best = total;
    }
  }
  return top;
}

/** "general" → "General" — expense categories are stored lower-case. */
export function categoryLabel(category: string): string {
  return category.slice(0, 1).toUpperCase() + category.slice(1);
}

/** "V-mount batteries ×2 · Gear · MRG Brand Package" — missing parts drop out. */
export function expenseRowMeta(exp: {
  description: string | null;
  category: string;
  project_name: string | null;
}): string {
  return [exp.description, categoryLabel(exp.category), exp.project_name].filter(Boolean).join(' · ');
}

export interface ExpenseRowRight {
  amount: string;
  /** "4 Sep ⎙" — the glyph only when a receipt is on file. */
  date: string;
  receipt: boolean;
}

export function expenseRowRight(
  exp: { expense_date: string; receipt_url: string | null },
  amountCents: number,
): ExpenseRowRight {
  const receipt = !!exp.receipt_url;
  return {
    amount: formatMoneyExact(amountCents),
    date: `${formatDayMonth(exp.expense_date)}${receipt ? ' ⎙' : ''}`,
    receipt,
  };
}

/** Right-hand total on an invoice status group — rounded, like the desktop one. */
export function invoiceGroupValue(amounts: number[]): string {
  return formatMoney(amounts.reduce((sum, c) => sum + c, 0));
}

/** Right-hand total on an expense month group — exact cents ("$412.90"). */
export function expenseGroupValue(amounts: number[]): string {
  return formatMoneyExact(amounts.reduce((sum, c) => sum + c, 0));
}

/** "2026-09" → "September 2026". */
export function expenseMonthLabel(key: string): string {
  return `${MONTHS_FULL[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
}

/** The Finance list's whole filter state — exactly the keys the page already
    serialises into a saved view, so a view round-trips identically. */
export interface FinanceFilters {
  tab: 'invoices' | 'expenses' | 'reports';
  query: string;
  client: string | null;
  category: string | null;
  kind?: InvoiceKind | '';
  unpaid?: boolean;
  undated?: boolean;
}

export const financeFiltersDefaults = (): FinanceFilters => ({
  tab: 'invoices',
  query: '',
  client: null,
  category: null,
});

/** Draft → saved-view params (empty values drop out in `normalizeParams`). */
export function financeSavedParams(f: FinanceFilters): Record<string, string | null> {
  return { ...(f.kind ? { kind: f.kind } : {}), tab: f.tab, query: f.query, client: f.client, category: f.category, ...(f.unpaid ? { unpaid: '1' } : {}), ...(f.undated ? { undated: '1' } : {}) };
}

/** Saved-view params → draft, mirroring the page's own `applyView`. */
export function financeFromSaved(p: Record<string, string>): FinanceFilters {
  return {
    tab: p.tab === 'expenses' || p.tab === 'reports' ? p.tab : 'invoices',
    query: p.query ?? '',
    client: p.client || null,
    category: p.category || null,
    ...(INVOICE_KINDS.includes(p.kind as InvoiceKind) ? { kind: p.kind as InvoiceKind } : {}),
    ...(p.unpaid === '1' ? { unpaid: true } : {}),
    ...(p.undated === '1' ? { undated: true } : {}),
  };
}
